import { type CSSProperties, type FC } from "react";

import { ENTITY_TYPE } from "../../../settings/settings.js";
import { type IAdvancedSearchFieldProps } from "./AdvancedSearchCriteria.js";
import { type IOptionObject } from "./CheckboxMultiSelect/Option.js";
import CheckboxSelect from "./CheckboxSelect.js";

/**
 * Two input fields for genomes to construct a variant profile filter.
 * First field contains all genomes where the variant must be present.
 * Second field contains all genomes where the variant must be absent.
 */
const AdvancedSearchVariantProfile: FC<IAdvancedSearchFieldProps> = (props: IAdvancedSearchFieldProps) => {
  const spanStyle: CSSProperties = Object.assign(
    { position: "relative" },
    !props.verticalLayout && ({ float: "left", width: "50%", paddingRight: "5px" } as CSSProperties),
  );

  // FIXME: Propose a better name for "value" properties?
  const value1 = (props.filterOperation.value as string[]) || [];
  const value2 = (props.filterOperation.value2 as string[]) || [];

  let searchAttributes = props.field.searchAttribute.values as unknown as IOptionObject[];

  if (props.tableQuerySpec?.resultType === ENTITY_TYPE.VCF_VARIANT) {
    const selectedGenomes = props.field.searchAttribute.values.filter((v) =>
      props.tableQuerySpec.restrictions.variantGenomeIds.find((i) => i === Number(v.key)),
    );
    searchAttributes = selectedGenomes;
  }

  // create the two value lists for the multi selects which
  // provide only genomes that have not been assigned to one of each other yet.
  // so we have only mutually exclusive criteria
  const optionsPresent = searchAttributes.filter((v) => value2.findIndex((x) => v.label === x) < 0);
  const optionsAbsent = searchAttributes.filter((v) => value1.findIndex((x) => v.label === x) < 0);

  // we need an array of complete IKeyLabels also for the selected values
  const selectedOptionsPresent = searchAttributes.filter((x) => value1.findIndex((v) => x.label === v) !== -1);
  const selectedOptionsAbsent = searchAttributes.filter((x) => value2.findIndex((v) => x.label === v) !== -1);

  // TODO: Probably a better idea is to have a unique placeholder property for CheckboxSelect
  //       which is a callback with the numerical quantity as parameter instead of having multiple placeholders?
  const totalNumberOfOptions = searchAttributes.length;
  const manySelectedFormatPresent = `%d genome${selectedOptionsPresent.length > 1 ? "s" : ""} selected`;
  const manySelectedFormatAbsent = `%d genome${selectedOptionsAbsent.length > 1 ? "s" : ""} selected`;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: props.verticalLayout ? "column" : "row",
        rowGap: props.verticalLayout ? 5 : null,
        columnGap: props.verticalLayout ? null : 5,
      }}
    >
      <div>
        <span> Must be present in </span>
        <CheckboxSelect
          uniqueKey={"present"}
          showFilter={optionsPresent.length > 1}
          showSelectAll={true}
          value={selectedOptionsPresent}
          options={optionsPresent}
          placeholder={optionsPresent.length !== 0 ? "Select genomes" : "No genomes left to select"}
          numberDisplayed={1}
          valueKey="key"
          labelKey="label"
          onChange={(option: IOptionObject[]) => {
            props.onChangeState({ value: option.map((innerOption: IOptionObject) => innerOption.label) });
          }}
          allSelectedPlaceholder={
            totalNumberOfOptions === selectedOptionsPresent.length
              ? "All %d genomes selected"
              : manySelectedFormatPresent
          }
          manySelectedPlaceholder={manySelectedFormatPresent}
          defaultFocusFilter={true}
          renderItemLabel={true}
        />
      </div>

      <div>
        <span> Must be absent in </span>
        <CheckboxSelect
          uniqueKey={"absent"}
          showFilter={optionsAbsent.length > 1}
          showSelectAll={true}
          value={selectedOptionsAbsent}
          options={optionsAbsent}
          placeholder={optionsAbsent.length !== 0 ? "Select genomes" : "No genomes left to select"}
          numberDisplayed={1}
          valueKey="key"
          labelKey="label"
          onChange={(option: IOptionObject[]) => {
            props.onChangeState({ value2: option.map((innerOption: IOptionObject) => innerOption.label) });
          }}
          allSelectedPlaceholder={
            totalNumberOfOptions === selectedOptionsAbsent.length ? "All %d genomes selected" : manySelectedFormatAbsent
          }
          manySelectedPlaceholder={manySelectedFormatAbsent}
          defaultFocusFilter={true}
          renderItemLabel={true}
        />
      </div>
    </div>
  );
};

export default AdvancedSearchVariantProfile;
